import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Copy, Trash2, Edit3, Eye, ExternalLink, Flame } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const STATUS_STYLE = {
  PUBLISHED: "text-brand border-brand/40 bg-brand/10",
  DRAFT: "text-slate-400 border-ink-600 bg-ink-800",
  SCHEDULED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  UNPUBLISHED: "text-red-400 border-red-500/30 bg-red-500/10",
};

export default function Posts() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useState("");

  const load = () => {
    const params = filter === "ALL" ? {} : { status: filter };
    api.get("/admin/posts", { params }).then((r) => setPosts(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);

  const duplicate = async (id) => {
    try { await api.post(`/admin/posts/${id}/duplicate`); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try { await api.delete(`/admin/posts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleTrending = async (p) => {
    const next = !p.is_trending;
    setPosts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_trending: next } : x)));
    try {
      await api.post(`/admin/posts/${p.id}/flag`, { field: "is_trending", value: next });
      toast.success(next ? "Pinned to Trending Now" : "Removed from Trending Now");
    } catch (e) {
      setPosts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_trending: !next } : x)));
      toast.error(apiErr(e));
    }
  };

  const filtered = posts.filter((p) => (p.title || "").toLowerCase().includes(q.toLowerCase()));
  const counts = ["ALL", "PUBLISHED", "DRAFT", "SCHEDULED", "UNPUBLISHED"];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="label-mono mb-1">Content</div>
          <h1 className="font-display font-extrabold text-white text-3xl">Posts</h1>
        </div>
        <Link to="/admin/posts/new" data-testid="posts-new" className="inline-flex items-center gap-2 bg-brand text-brand-dark font-bold text-sm px-5 py-2.5 rounded-full hover:bg-brand-neon transition-colors"><Plus size={16} /> New Post</Link>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-1 bg-ink-900 border border-ink-600 rounded-full p-1">
          {counts.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c.toLowerCase()}`} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === c ? "bg-brand text-brand-dark" : "text-slate-400 hover:text-white"}`}>{c}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-ink-900 border border-ink-600 rounded-full px-4 py-2 flex-1 min-w-[200px] max-w-xs">
          <Search size={15} className="text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="posts-search" placeholder="Search titles..." className="bg-transparent outline-none text-sm text-white flex-1 placeholder:text-slate-600" />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-brand/25 bg-brand/5 p-3.5 mb-5">
        <Flame size={16} className="text-brand shrink-0 mt-0.5" />
        <p className="text-xs text-slate-300 leading-relaxed">Tap the flame to pin a post to <span className="text-brand font-semibold">Trending Now</span> on the homepage. Pinned posts always show first. Any empty slots fill automatically with your most viewed articles, so Trending Now is never empty.</p>
      </div>

      <div className="rounded-xl border border-ink-600 bg-ink-900 overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-ink-700 text-left">
                <th className="px-5 py-3 text-xs font-mono-label text-slate-500 uppercase">Title</th>
                <th className="px-4 py-3 text-xs font-mono-label text-slate-500 uppercase">Category</th>
                <th className="px-4 py-3 text-xs font-mono-label text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-mono-label text-slate-500 uppercase">Views</th>
                <th className="px-4 py-3 text-xs font-mono-label text-slate-500 uppercase text-center">Trending</th>
                <th className="px-4 py-3 text-xs font-mono-label text-slate-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="posts-table">
              {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No posts found.</td></tr>}
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-ink-700/60 last:border-0 hover:bg-ink-800/40 transition-colors">
                  <td className="px-5 py-3.5">
                    <Link to={`/admin/posts/${p.id}`} className="text-white font-medium hover:text-brand transition-colors line-clamp-1">{p.title || "Untitled"}</Link>
                  </td>
                  <td className="px-4 py-3.5 text-slate-400 capitalize whitespace-nowrap">{(p.category || "-").replace(/-/g, " ")}</td>
                  <td className="px-4 py-3.5"><span className={`text-[10px] font-mono-label px-2.5 py-1 rounded-full border ${STATUS_STYLE[p.status] || STATUS_STYLE.DRAFT}`}>{p.status}</span></td>
                  <td className="px-4 py-3.5 text-slate-300">{(p.views || 0).toLocaleString()}</td>
                  <td className="px-4 py-3.5 text-center">
                    <button onClick={() => toggleTrending(p)} data-testid={`trending-toggle-${p.id}`} aria-pressed={!!p.is_trending} title={p.is_trending ? "Pinned to Trending Now" : "Pin to Trending Now"}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${p.is_trending ? "text-brand border-brand/50 bg-brand/10" : "text-slate-500 border-ink-600 hover:text-brand hover:border-brand/50"}`}>
                      <Flame size={15} fill={p.is_trending ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {p.status === "PUBLISHED" && <a href={`/article/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-brand" title="View"><Eye size={15} /></a>}
                      <Link to={`/admin/posts/${p.id}`} className="p-1.5 text-slate-400 hover:text-brand" title="Edit" data-testid={`edit-${p.id}`}><Edit3 size={15} /></Link>
                      <button onClick={() => duplicate(p.id)} className="p-1.5 text-slate-400 hover:text-brand" title="Duplicate"><Copy size={15} /></button>
                      <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-1.5 text-slate-400 hover:text-red-400" title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
